import { reportCapabilities } from './api';

/**
 * Co ta przeglądarka umie zdekodować.
 *
 * Pytamy **przeglądarkę**, a nie zgadujemy z nazwy silnika: `MediaSource` zna
 * odpowiedź, a lista „Safari nie umie AV1" starzeje się z każdą aktualizacją
 * systemu. Serwer używa tego, żeby nie przekodowywać materiału, który u tej osoby
 * i tak by się odtworzył — a przekodowanie obrazu jest najdroższą operacją w całym
 * potoku.
 *
 * Raport wysyłamy **raz na urządzenie**, nie przy każdym wejściu: odpowiedź zmienia
 * się tylko przy aktualizacji przeglądarki, a serwer i tak uznaje go za nieważny po
 * miesiącu. Znacznik w `localStorage` nosi wersję raportu, więc dopisanie kodeka
 * wymusi wysłanie od nowa.
 */

const STORAGE_KEY = 'anisync.capabilities';

/** Podbij przy zmianie zestawu pytań — stary znacznik przestanie wtedy zwalniać. */
const REPORT_VERSION = 1;

/**
 * Ciągi typów do sprawdzenia.
 *
 * Po dwa na kodek, bo obsługa zależy od **profilu**, nie od samej nazwy: HEVC
 * w profilu Main działa na Apple, a w Main10 już nie wszędzie; AV1 zgłasza się
 * inaczej w MP4 i w WebM. Pytamy o warianty najczęstsze w anime i przyjmujemy
 * obsługę, gdy przejdzie którykolwiek.
 */
const PROBES = {
  hevc: ['video/mp4; codecs="hvc1.1.6.L93.B0"', 'video/mp4; codecs="hev1.2.4.L120.B0"'],
  av1: ['video/mp4; codecs="av01.0.05M.08"', 'video/webm; codecs="av01.0.05M.08"'],
  matroska: ['video/x-matroska; codecs="avc1.42E01E"', 'video/x-matroska'],
} as const;

export interface Capabilities {
  hevc: boolean;
  av1: boolean;
  matroska: boolean;
}

export function probeCapabilities(): Capabilities {
  return {
    hevc: anySupported(PROBES.hevc),
    av1: anySupported(PROBES.av1),
    matroska: anySupported(PROBES.matroska),
  };
}

/**
 * Wysyła raport, jeśli jeszcze nie wysłano go z tego urządzenia.
 *
 * Porażka jest **milcząca**: brak raportu nie psuje niczego poza tym, że serwer
 * decyduje ostrożniej. Komunikat o tym, że „nie udało się zgłosić zdolności
 * dekodowania", nic widzowi nie mówi i nie ma na niego reakcji.
 */
export function reportCapabilitiesOnce(): void {
  try {
    if (window.localStorage.getItem(STORAGE_KEY) === String(REPORT_VERSION)) return;
  } catch {
    // Tryb prywatny bez magazynu: wyślemy raport raz na wejście i tyle.
  }

  const capabilities = probeCapabilities();
  void reportCapabilities(capabilities)
    .then(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(REPORT_VERSION));
      } catch {
        // Bez zapisu znacznika raport poleci ponownie — to jedno żądanie, nie problem.
      }
    })
    .catch(() => undefined);
}

function anySupported(types: ReadonlyArray<string>): boolean {
  for (const type of types) {
    /**
     * `MediaSource.isTypeSupported` przed `canPlayType`.
     *
     * To pierwsze odpowiada twardym „tak/nie", a drugie zwraca `"maybe"`, którego
     * nie da się uczciwie zamienić na wartość logiczną — a od tej decyzji zależy,
     * czy widz dostanie obraz, czy czarny ekran.
     */
    if (typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported(type)) return true;
  }

  // Zapas dla przeglądarek bez `MediaSource` (starsze Safari na iOS): tam
  // `canPlayType` jest jedynym pytaniem, jakie da się zadać.
  const probe = document.createElement('video');
  for (const type of types) {
    if (probe.canPlayType(type) === 'probably') return true;
  }
  return false;
}
